import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Car, Plus, ArrowLeft, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const VehicleManagement = () => {
  const { API, sessionToken } = useContext(AuthContext);
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [formData, setFormData] = useState({
    make: '',
    model: '',
    year: new Date().getFullYear(),
    color: '',
    license_plate: '',
    mileage: 0,
    vin: '',
    engine: '',
    transmission: '',
    country: 'Costa Rica'
  });

  const fetchVehicles = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/vehicles`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setVehicles(response.data);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    }
  }, [API, sessionToken]);

  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  const handleAddVehicle = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API}/vehicles`, formData, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });

      toast.success('Vehículo agregado exitosamente');
      setShowAddDialog(false);
      setFormData({
        make: '',
        model: '',
        year: new Date().getFullYear(),
        color: '',
        license_plate: '',
        mileage: 0,
        vin: '',
        engine: '',
        transmission: '',
        country: 'Costa Rica'
      });
      fetchVehicles();
    } catch (error) {
      toast.error('Error al agregar vehículo');
      console.error(error);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="container mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" onClick={() => navigate('/client/dashboard')} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit' }}>
              Mis Vehículos
            </h1>
          </div>

          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button data-testid="add-vehicle-dialog-btn">
                <Plus className="w-4 h-4 mr-2" />
                Agregar Vehículo
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Agregar Nuevo Vehículo</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddVehicle} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Marca</Label>
                    <Input
                      value={formData.make}
                      onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                      required
                      data-testid="make-input"
                    />
                  </div>
                  <div>
                    <Label>Modelo</Label>
                    <Input
                      value={formData.model}
                      onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                      required
                      data-testid="model-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Año</Label>
                    <Input
                      type="number"
                      value={formData.year}
                      onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                      required
                      data-testid="year-input"
                    />
                  </div>
                  <div>
                    <Label>Color</Label>
                    <Input
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      required
                      data-testid="color-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Placa</Label>
                    <Input
                      value={formData.license_plate}
                      onChange={(e) => setFormData({ ...formData, license_plate: e.target.value })}
                      required
                      data-testid="license-plate-input"
                    />
                  </div>
                  <div>
                    <Label>Kilometraje</Label>
                    <Input
                      type="number"
                      value={formData.mileage}
                      onChange={(e) => setFormData({ ...formData, mileage: parseInt(e.target.value) })}
                      required
                      data-testid="mileage-input"
                    />
                  </div>
                </div>

                <div>
                  <Label>VIN (Opcional)</Label>
                  <Input
                    value={formData.vin}
                    onChange={(e) => setFormData({ ...formData, vin: e.target.value })}
                    data-testid="vin-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Motor (Opcional)</Label>
                    <Input
                      value={formData.engine}
                      onChange={(e) => setFormData({ ...formData, engine: e.target.value })}
                      placeholder="2.0L"
                      data-testid="engine-input"
                    />
                  </div>
                  <div>
                    <Label>Transmisión (Opcional)</Label>
                    <Input
                      value={formData.transmission}
                      onChange={(e) => setFormData({ ...formData, transmission: e.target.value })}
                      placeholder="Automática"
                      data-testid="transmission-input"
                    />
                  </div>
                </div>

                <Button type="submit" className="w-full" data-testid="submit-vehicle-btn">
                  Agregar Vehículo
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {vehicles.length === 0 ? (
          <Card className="p-12 text-center">
            <Car className="w-16 h-16 mx-auto text-slate-400 mb-4" />
            <h3 className="text-xl font-semibold mb-2">No tienes vehículos registrados</h3>
            <p className="text-slate-600 mb-6">Agrega tu primer vehículo para comenzar</p>
            <Button onClick={() => setShowAddDialog(true)} data-testid="add-first-vehicle-btn">
              <Plus className="w-4 h-4 mr-2" />
              Agregar Vehículo
            </Button>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {vehicles.map((vehicle) => (
              <Card key={vehicle.id} className="p-6 hover:shadow-lg transition-shadow" data-testid={`vehicle-item-${vehicle.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                      <Car className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">{vehicle.make} {vehicle.model}</h3>
                      <p className="text-sm text-slate-600">{vehicle.year}</p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Placa:</span>
                    <span className="font-medium">{vehicle.license_plate}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Color:</span>
                    <span className="font-medium">{vehicle.color}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Kilometraje:</span>
                    <span className="font-medium">{vehicle.mileage} km</span>
                  </div>
                  {vehicle.vin && (
                    <div className="flex justify-between">
                      <span className="text-slate-600">VIN:</span>
                      <span className="font-medium text-xs">{vehicle.vin.slice(0, 10)}...</span>
                    </div>
                  )}
                  {vehicle.insurance_info?.insurance_company && (
                    <div className="mt-2 pt-2 border-t space-y-2" data-testid={`vehicle-insurance-${vehicle.id}`}>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Aseguradora:</span>
                        <span className="font-medium">{vehicle.insurance_info.insurance_company}</span>
                      </div>
                      {vehicle.insurance_info.policy_number && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">Póliza:</span>
                          <span className="font-medium">{vehicle.insurance_info.policy_number}</span>
                        </div>
                      )}
                      {vehicle.insurance_info.deductible > 0 && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">Deducible:</span>
                          <span className="font-medium">₡{Number(vehicle.insurance_info.deductible).toLocaleString('es-CR')}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t">
                  <Button 
                    variant="outline" 
                    className="w-full" 
                    size="sm"
                    onClick={() => navigate(`/client/car-passport/${vehicle.id}`)}
                    data-testid={`car-passport-btn-${vehicle.id}`}
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    Ver Car Passport
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VehicleManagement;